/**
 * The requester-side announcement banner — Step 51.
 *
 * Kai: announcements are *"mainly for end users, rather than agents, but both
 * should see them"*, so this is the surface that actually matters. Same
 * markup as the agent banner (`AnnouncementStrip`), different door: the
 * portal domain gets its own fetch pair and its own query key, because a
 * signed-in agent browsing their own portal must not be served the agent
 * cache — the server answers a different question at each endpoint.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { portalFetch, portalSend } from '../../lib/portalApi';
import { qk } from '../../lib/queryClient';
import { AnnouncementStrip, type Announcement } from './AnnouncementBanner';

export function PortalAnnouncementBanner() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: qk.announcements.portal,
    queryFn: () => portalFetch<{ entries: Announcement[] }>('/api/portal/announcements'),
    staleTime: 60_000,
    // A requester who is not signed in has no announcements to miss; the
    // banner simply does not render rather than bouncing them to a login.
    retry: false,
  });
  const dismiss = useMutation({
    mutationFn: (id: string) => portalSend(`/api/portal/announcements/${id}/dismiss`, 'POST'),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.announcements.portal }),
  });

  // `card`, not the agent shell's top strip: Kai wanted these *"within the
  // page for the requester portal end"*.
  return (
    <AnnouncementStrip
      entries={q.data?.entries ?? []}
      onDismiss={(id) => dismiss.mutate(id)}
      variant="card"
    />
  );
}
